import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { Button, ButtonSkeleton, Panel, PanelBody, TextSkeleton } from '@gitbook/spine-react';

import { apiQuery } from '../../state';

type SiteAdsRevenuePanelProps = {
    organizationId: string;
    site: api.Site;
};

/**
 * Panel to display the ads revenue for the site
 */
export function SiteAdsRevenuePanel(props: SiteAdsRevenuePanelProps) {
    const { site } = props;

    if (site.ads?.status !== 'live') {
        return null;
    }

    return (
        <Panel>
            <PanelBody>
                <React.Suspense
                    fallback={
                        <div className="flex flex-row gap-4">
                            <div className="flex flex-1 flex-col gap-1">
                                <TextSkeleton className="text-ui-heading-medium">
                                    Amount
                                </TextSkeleton>
                                <TextSkeleton className="text-ui-standout">
                                    Revenue over the past 30 days
                                </TextSkeleton>
                            </div>
                            <ButtonSkeleton label="Configure payout" />
                        </div>
                    }
                >
                    <SiteAdsRevenuePanelContent {...props} />
                </React.Suspense>
            </PanelBody>
        </Panel>
    );
}

function SiteAdsRevenuePanelContent(props: SiteAdsRevenuePanelProps) {
    const { organizationId, site } = props;

    const { data } = useSuspenseQuery(
        apiQuery('getAdsInsightsForSiteById', {
            organizationId,
            siteId: site.id,
        })
    );

    return (
        <div className="flex flex-row gap-4">
            <div className="flex flex-1 flex-col gap-1">
                <h2 className="text-[2rem] leading-6">{data.values.at(0)?.stats.revenue}</h2>
                <div className="text-secondary text-ui-standout">
                    Revenue during the last 30 days
                </div>
                <p className="text-secondary text-ui-base">
                    You can track and manage payouts for the account{' '}
                    <b className="font-semibold">{data.account.email}</b> on our partner platform.
                </p>
            </div>
            <div>
                <Button
                    kind="secondary"
                    label="Configure payout"
                    leadingIcon={Icon.Settings}
                    trailingIcon={Icon.ArrowUpRight}
                    href={data.account.url}
                    target="_blank"
                    onPress={() => {
                        analytics.logEvent(
                            analytics.createSiteEvent(site, {
                                type: 'site_ads_open_payout',
                            })
                        );
                    }}
                />
            </div>
        </div>
    );
}
